import { computed, effect, inject, Injectable, Injector, untracked } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { PlatformService } from '@okcargo-apps/shared-infrastructure-ionic';
import { firstValueFrom } from 'rxjs';

/**
 * @description
 * Shows a persistent toast while there is no connectivity, plus a blocking layer below it when
 * running in the browser.
 *
 * The toast is always shown, on native and in the browser. In the browser a lazy chunk that could
 * not be fetched stays broken until the document is reloaded, so the interaction is blocked by a
 * white translucent layer (`OfflineOverlayComponent`) to prevent those failed fetches. The toast
 * stays visible above the layer, and the app content is made inert (pointer and keyboard) while
 * the layer is shown.
 */
/** Text used when the translations are not available. */
const FALLBACK_MESSAGES: Record<'es' | 'en', string> = {
  es: 'Sin conexión a internet',
  en: 'No internet connection',
};

@Injectable({
  providedIn: 'root',
})
export class ConnectivityNoticeService {
  private readonly connectivity = inject(ConnectivityService);
  private readonly platform = inject(PlatformService);
  private readonly toastCtrl = inject(ToastController);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);

  /** True when the blocking layer must be rendered below the toast (browser without connection). */
  public readonly isOverlayVisible = computed(() => !this.platform.isHybrid && this.connectivity.isOffline());

  private toast?: HTMLIonToastElement;
  private started = false;

  /**
   * Starts watching the connectivity status. Call it once the app is ready
   * (after the platform is ready and the translations can be loaded).
   */
  public start(): void {
    if (this.started) {
      return;
    }

    this.started = true;

    effect(
      () => {
        // untracked: only the connectivity status must be tracked here. Reading anything else
        // inside show/hide could make this effect re-run on unrelated signal changes.
        if (this.connectivity.isOffline()) {
          untracked(() => void this.show());
        } else {
          untracked(() => void this.hide());
        }
      },
      { injector: this.injector },
    );
  }

  private async show(): Promise<void> {
    if (!this.isNoticeNeeded()) {
      return;
    }

    const message = await this.getMessage();

    // The connection may have been restored while the translation was loading.
    if (!this.isNoticeNeeded()) {
      return;
    }

    const toast = await this.toastCtrl.create({
      message,
      color: 'warning',
      position: 'top',
      duration: 0,
      // Scopes the look of this toast: bold, centred text (see theme/okcargo-ion-toast.scss).
      cssClass: 'connectivity-toast',
    });

    // The connection may also have been restored while the toast was being created.
    if (!this.isNoticeNeeded()) {
      await toast.dismiss();
      return;
    }

    this.toast = toast;
    await toast.present();
  }

  private isNoticeNeeded(): boolean {
    return !this.toast && this.connectivity.isOffline();
  }

  /**
   * Message of the toast. The i18n assets are cached on demand, so they may be missing when the app
   * starts offline for the first time: an embedded text is used then, and also when the loader fails
   * (the notice must never show a raw translation key).
   */
  private async getMessage(): Promise<string> {
    const key = 'general.no_connection';

    try {
      const translated = await firstValueFrom(this.translate.get(key));

      if (translated && translated !== key) {
        return translated;
      }
    } catch (error) {
      console.warn('[ConnectivityNoticeService] Could not load the notice translation:', error);
    }

    const language = (navigator.language || navigator.languages?.[0] || 'en').toLowerCase();

    return FALLBACK_MESSAGES[language.startsWith('es') ? 'es' : 'en'];
  }

  private async hide(): Promise<void> {
    const toast = this.toast;

    if (!toast) {
      return;
    }

    this.toast = undefined;
    await toast.dismiss();
  }
}
